/**
 * 본문 인라인 KB 인용 태그.
 *  - [A] [B] ... : Citation 을 Knowledge Base > Unstructured 로 연다.
 *                  글자는 순서(A=첫째)라 해당 출처의 확장자 배지를 앞에 붙인다.
 *  - [RDB]       : Citation 을 Knowledge Base > Structured > RDB 로 연다. 아이콘 없음.
 * 붙어 오는 [A][B] 는 숫자 인용과 같이 태그 하나로 합쳐 배지를 겹쳐 보여준다.
 */
import useAnswerSideBar from "@/hooks/ai-agent/use-answer-side-bar";
import useAnswerSideBarStore from "@/stores/answer-side-bar";
import type { KbCitationTab, KbContentData, KbUnstructuredItem } from "@/utils/knowledge-content";
import {
  FileBadgeStack,
  dedupeBadges,
  getFileBadge,
  type FileBadgeInfo,
} from "@/components/ui/file-type-indicator";
import { getSourceFavicons } from "@/utils/reference";
import FaviconImage from "./favicon-image";

/** A=0, B=1 ... 글자 순서로 unstructured 출처를 고른다. */
const letterIndex = (label: string): number => label.charCodeAt(0) - 65;

/* sdf 는 문서 번호를 글자("A")로 보내기도 해서 번호가 같은 문서를 먼저 찾고, 없으면 순서로 고른다 */
const findSource = (reports: KbUnstructuredItem[] | null | undefined, label: string) =>
  reports?.find((r) => r.reference_number === label) ?? reports?.[letterIndex(label)];

const KbCitationTag = ({
  labels,
  tab,
  knowledgeContent,
  /** 메시지 단위 고유 키. 같은 탭 태그끼리 강조가 섞이지 않게 한다. */
  instanceKey,
}: {
  labels: string[];
  tab: KbCitationTab;
  knowledgeContent?: KbContentData | null;
  instanceKey?: string;
}) => {
  const { handleAnswerSideBarOpen } = useAnswerSideBar();
  const isOpen = useAnswerSideBarStore((s) => s.isOpen);
  const selectedReferenceNumber = useAnswerSideBarStore((s) => s.selectedReferenceNumber);

  const sourceKey = `kb-${tab}-${labels.join("")}-${instanceKey ?? ""}`;
  const active = isOpen && selectedReferenceNumber === sourceKey;

  /* [RDB] 는 아이콘 없이 글자만. 글자 태그는 가리키는 출처를 아이콘으로 보여준다. */
  const sources =
    tab === "unstructured"
      ? labels.map((label) => findSource(knowledgeContent?.unstructuredReport, label))
      : [];

  /* 파일 출처는 확장자 배지. 같은 확장자는 하나로 합친다. */
  const badges: FileBadgeInfo[] = dedupeBadges(
    sources.map((s) => getFileBadge(s?.title)).filter((b): b is FileBadgeInfo => b !== null)
  );

  /* url 만 있는 출처는 파비콘으로. 파일 제외·도메인 중복 제거는 출처 버튼과 같은 함수. */
  const favicons = getSourceFavicons(
    sources
      .filter((s) => s?.url)
      .map((s) => ({
        reference_number: 0,
        title: s?.title ?? "",
        url: s?.url ?? "",
        content: "",
      }))
  );

  return (
    <button
      type="button"
      onClick={() =>
        handleAnswerSideBarOpen("answer-reference", [], true, knowledgeContent ?? null, sourceKey, tab)
      }
      /* align-text-bottom: 버튼 아래를 글자 descender 선에 맞춘다(ReferenceTag 와 같은 규칙). */
      className={`rounded-[8px] px-2 py-1.5 h-[24px] inline-flex items-center gap-[6px] align-text-bottom ${
        active ? "bg-[#C5F7EE]" : "bg-[#F8F8F8]"
      }`}
    >
      {badges.length > 0 && <FileBadgeStack badges={badges} />}
      {favicons.length > 0 && (
        <div className="flex items-center">
          {favicons.map((favicon, index) => (
            <FaviconImage
              key={favicon}
              src={favicon}
              alt={`source-${index}`}
              className={`w-5 h-5 rounded-full object-cover border-2 border-white shrink-0 ${
                index > 0 ? "-ml-[6.67px]" : ""
              }`}
            />
          ))}
        </div>
      )}
      <span className="font-semibold text-[14px] leading-none text-grey-40 flex items-center justify-center">
        {labels.map((label) => `[${label}]`).join("")}
      </span>
    </button>
  );
};

export default KbCitationTag;
